import {
  Quat,
  NumberParameter,
  MultiChoiceParameter,
  Operator,
  OperatorOutputMode,
  Registry,
  XfoOperatorInput,
  XfoOperatorOutput,
} from '@zeainc/zea-engine'

/** An operator for aiming items at targets.
 * @extends Operator
 */
class AimOperator extends Operator {
  weightParam = new NumberParameter('Weight', 1)
  axisParam = new MultiChoiceParameter('Axis', 0, ['+X Axis', '-X Axis', '+Y Axis', '-Y Axis', '+Z Axis', '-Z Axis'])
  stretchParam = new NumberParameter('Stretch', 0.0)
  initialDistParam = new NumberParameter('Initial Dist', 1.0)

  targetInput = new XfoOperatorInput('Target')
  xfoInputOutput = new XfoOperatorOutput('InputOutput', OperatorOutputMode.OP_READ_WRITE)
  /**
   * Create a gears operator.
   * @param {string} name - The name value.
   */
  constructor(name?: string) {
    super(name)

    this.addParameter(this.weightParam)
    this.addParameter(this.axisParam)

    this.addParameter(this.stretchParam)
    this.addParameter(this.initialDistParam)
    // this.addParameter(new XfoParameter('Target'))
    this.addInput(this.targetInput)
    this.addOutput(this.xfoInputOutput)
  }

  /**
   * The resetStretchRefDist method.
   */
  resetStretchRefDist(): void {
    const target = this.targetInput.getValue()
    const output = this.getOutputByIndex(0)
    const xfo = output.getValue()
    const dist = target.tr.subtract(xfo.tr).length()
    this.initialDistParam.value = dist
  }

  /**
   * The evaluate method.
   */
  evaluate(): void {
    const weight = this.weightParam.value
    const axis = this.axisParam.value
    const target = this.targetInput.getValue()
    const output = this.getOutputByIndex(0)
    const xfo = output.getValue()
    const dir = target.tr.subtract(xfo.tr)
    const dist = dir.length()
    if (dist < 0.000001) return
    dir.scaleInPlace(1 / dist)
    let vec
    switch (axis) {
      case 0:
        vec = xfo.ori.getXaxis()
        break
      case 1:
        vec = xfo.ori.getXaxis().negate()
        break
      case 2:
        vec = xfo.ori.getYaxis()
        break
      case 3:
        vec = xfo.ori.getYaxis().negate()
        break
      case 4:
        vec = xfo.ori.getZaxis()
        break
      case 5:
        vec = xfo.ori.getZaxis().negate()
        break
    }
    let align = new Quat()
    align.setFrom2Vectors(vec, dir)
    align.alignWith(new Quat())
    if (weight < 1.0) align = new Quat().lerp(align, weight)
    xfo.ori = align.multiply(xfo.ori)
    const stretch = this.stretchParam.value
    if (stretch > 0.0) {
      const initialDist = this.initialDistParam.value
      // Scale the output to reach towards the target.
      // Note: once the base xfo is re-calculated, then
      // we can make this scale relative. (e.g. *= sc)
      // This will happen once GalcGlibalXfo is the base
      // operator applied to GlobalXfo param.
      // Until then, we must reset scale manually here.
      const sc = 1.0 + (dist / initialDist - 1.0) * stretch
      switch (axis) {
        case 0:
        case 1:
          xfo.sc.x = sc
          break
        case 2:
        case 3:
          xfo.sc.y = sc
          break
        case 4:
        case 5:
          xfo.sc.z = sc
          break
      }
      // console.log("AimOperator.evaluate:", xfo.sc.toString())
    }
    output.setClean(xfo)
  }
}

Registry.register('AimOperator', AimOperator)

export { AimOperator }
